import Image from 'next/image';
import { useMemo } from 'react';

import { Text } from '@/design-system/components/ui';
import useGetChainInfo from '@/hooks/useGetChainInfo';
import { getAddressScannerUrl, maskAddressValue } from '@/utils/format';

import { EventTriggerType, Trigger } from '../../domain';
import { TRIGGER_TYPE_MAP } from '../../domain/mappers';
import { TriggerExternalLinkDetail } from './components/TriggerExternalLinkDetail';

interface Props {
  trigger: Trigger;
  triggerType: EventTriggerType;
}

export const LiquidPoolTriggerDetails = ({ trigger, triggerType }: Props) => {
  const { getChainIconUrl } = useGetChainInfo();

  const poolInfo = useMemo(() => {
    if (!trigger.signature) return { address: '', chainId: '' };

    const parts = trigger.signature.split('-');

    if (parts.length < 5) return { address: '', chainId: '' };

    const address = parts[parts.length - 2];
    const chainId = parts[parts.length - 1];

    return {
      address: address || '',
      chainId: chainId || '',
    };
  }, [trigger.signature]);

  const scannerUrl = useMemo(() => {
    if (!poolInfo.address || !poolInfo.chainId) return '';
    return getAddressScannerUrl('chainId', Number(poolInfo.chainId), poolInfo.address) || '';
  }, [poolInfo]);

  const chainIconUrl = useMemo(() => {
    if (!poolInfo.chainId) return '';
    return getChainIconUrl(Number(poolInfo.chainId)) || '';
  }, [poolInfo.chainId, getChainIconUrl]);

  const protocolLogo = useMemo(() => {
    switch (triggerType) {
      case EventTriggerType.HybraLiquidityV3:
        return '/assets/png/hybra.png';
      case EventTriggerType.HyperswapLiquidityV3:
        return '/assets/svg/hyperswap.svg';
      case EventTriggerType.PrjXLiquidityV3:
        return '/assets/webp/prjx.webp';
      case EventTriggerType.KittenSwapLiquidityV3:
        return '/assets/webp/kittenswap.webp';
      case EventTriggerType.UltrasolidLiquidityV3:
        return '/assets/webp/ultrasolid.webp';
      case EventTriggerType.UpheavalLiquidityV3:
        return '/assets/webp/upheaval.webp';
      case EventTriggerType.NestLiquidityV3:
        return '/assets/webp/nest.webp';
      default:
        return '/assets/png/uniswap.png';
    }
  }, [triggerType]);

  const triggerTypeDisplayName = useMemo(() => {
    const baseName = TRIGGER_TYPE_MAP[triggerType];
    return `${baseName} - Liquidity Pool`;
  }, [triggerType]);

  return (
    <div className="tw:flex tw:flex-row tw:gap-6">
      <div className="tw:flex tw:flex-col">
        <Text variant="body-sm" weight="bold" className="tw:mb-1">
          Type
        </Text>
        <div className="tw:flex tw:flex-row tw:gap-1 tw:items-center">
          <Image src={protocolLogo} width={20} height={20} alt={triggerType} />
          <Text variant="body-sm" className="tw:py-0.5">
            {triggerTypeDisplayName}
          </Text>
        </div>
      </div>

      <TriggerExternalLinkDetail
        label="Pool Address"
        value={
          <div className="tw:flex tw:flex-row tw:gap-1 tw:items-center">
            {chainIconUrl && <Image src={chainIconUrl} width={12} height={12} alt={`Chain ${poolInfo.chainId}`} />}
            <span>{maskAddressValue(poolInfo.address) ?? ''}</span>
          </div>
        }
        href={scannerUrl}
      />
    </div>
  );
};
